import type { ProfileData } from './types';

const object = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
const nullableNumber = (value: unknown) => value === null || (typeof value === 'number' && Number.isSafeInteger(value) && value > 0);
const values = (value: unknown): boolean => Array.isArray(value) && value.every(entry => {
  const field = object(entry);
  return field && typeof field.name === 'string' && (field.value === null || typeof field.value === 'string');
});

/** Nullable transport fields must be reviewed before a response can supply edit controls. */
export const profileData = (value: unknown, odrnumber: number): ProfileData | null => {
  const data = object(value);
  if (!data || data.odrnumber !== odrnumber || typeof data.blocked !== 'boolean' || !Array.isArray(data.links) || !Array.isArray(data.entities)) return null;
  if (!data.links.every(entry => {
    const link = object(entry);
    return link && nullableNumber(link.participant) && (link.active === null || typeof link.active === 'boolean');
  })) return null;
  if (!data.entities.every(entry => {
    const entity = object(entry);
    if (!entity || typeof entity.name !== 'string' || typeof entity.label !== 'string' || typeof entity.collection !== 'boolean'
      || !Array.isArray(entity.fields) || !Array.isArray(entity.records)) return false;
    if (!entity.fields.every(entry => {
      const field = object(entry);
      if (!field || typeof field.name !== 'string' || typeof field.label !== 'string' || typeof field.type !== 'string' || typeof field.required !== 'boolean'
        || typeof field.editable !== 'boolean' || !Array.isArray(field.options) || !field.options.every(option => typeof option === 'string')) return false;
      if (field.objectFields == null) return true;
      return Array.isArray(field.objectFields) && field.objectFields.every(entry => {
        const part = object(entry);
        return part && typeof part.name === 'string' && typeof part.label === 'string' && (part.format === null || typeof part.format === 'string');
      });
    })) return false;
    return entity.records.every(entry => {
      const record = object(entry);
      if (!record || typeof record.key !== 'string' || !record.key || typeof record.revision !== 'string' || !record.revision
        || typeof record.timestamp !== 'string' || typeof record.author !== 'string' || !nullableNumber(record.participant)
        || typeof record.needsSync !== 'boolean' || typeof record.removed !== 'boolean' || typeof record.canonical !== 'boolean'
        || !values(record.values) || !values(record.unifiedValues)) return false;
      if (record.references != null && (!Array.isArray(record.references) || !record.references.every(entry => {
        const reference = object(entry);
        return reference && typeof reference.name === 'string' && typeof reference.key === 'string' && values(reference.values);
      }))) return false;
      if (record.sources == null) return true;
      return Array.isArray(record.sources) && record.sources.every(entry => {
        if (entry === null) return true;
        const source = object(entry);
        return source && (source.values === null || values(source.values));
      });
    });
  })) return null;
  return value as ProfileData;
};
